import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import ApiService from "../services/ApiService";
import logo from "../assets/logo.png";
import "./PayPerUseSlotBooking.css";

const PayPerUseSlotBooking = () => {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get plan details from navigation state
  const { selectedPlan, selectedCarType, appliedCoupon } = location.state || {};
  // Booking calendar states
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [disabledDates, setDisabledDates] = useState([]);
  const [userBookings, setUserBookings] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  useEffect(() => {
    // Redirect if no plan data
    if (!selectedPlan || !selectedCarType) {
      navigate("/integrated-purchase-flow");
      return;
    }

    // Fetch disabled dates and user bookings
    fetchDisabledDates();
    fetchUserBookings();
  }, [selectedPlan, selectedCarType, navigate]);
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps
  const fetchDisabledDates = async () => {
    try {
      const response = await ApiService.getUpcomingDisabledDates();
      setDisabledDates(response.data || []);
    } catch (error) {
      console.error("Error fetching disabled dates:", error);
    }
  };
  const fetchUserBookings = async () => {
    try {
      const response = await ApiService.getUserBookings();
      setUserBookings(response.data || []);
    } catch (error) {
      console.error("Error fetching user bookings:", error);
    }
  };

  // Helper function to check if user already has a booking for this slot and date
  const hasExistingBooking = (slotId, date) => {
    const dateStr = date.toISOString().split("T")[0];
    return userBookings.some((booking) => {
      // Check for active bookings (not cancelled) on the same date and slot
      return (
        booking.status !== "cancelled" &&
        booking.bookingDate === dateStr &&
        booking.slot &&
        booking.slot.id === slotId
      );
    });
  };

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      const dateStr = selectedDate.toISOString().split("T")[0];
      const response = await ApiService.getAvailableSlots(dateStr);
      setAvailableSlots(response.data || []);
      setSelectedSlot(null);
    } catch (error) {
      console.error("Error fetching available slots:", error);
      setError("Failed to load available slots");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Helper function to check if a date is disabled
  const isDateDisabled = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    return disabledDates.some((disabledDate) => {
      let disabledDateStr;
      if (typeof disabledDate.date === "string") {
        disabledDateStr = disabledDate.date.split("T")[0];
      } else {
        disabledDateStr = new Date(disabledDate.date)
          .toISOString()
          .split("T")[0];
      }
      return disabledDateStr === dateStr;
    });
  };

  // Helper function to get disabled date reason
  const getDisabledDateReason = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    const disabledDate = disabledDates.find((disabledDate) => {
      let disabledDateStr;
      if (typeof disabledDate.date === "string") {
        disabledDateStr = disabledDate.date.split("T")[0];
      } else {
        disabledDateStr = new Date(disabledDate.date)
          .toISOString()
          .split("T")[0];
      }
      return disabledDateStr === dateStr;
    });
    return disabledDate?.reason || "Booking not available";
  };

  const handleDateSelect = (date) => {
    // Check if the date is disabled
    if (isDateDisabled(date)) {
      const reason = getDisabledDateReason(date);
      setError(`Cannot book on this date: ${reason}`);
      return;
    }

    setSelectedDate(date);
    setSelectedSlot(null);
    setError("");
    setSuccess("");
  };
  const handleSlotSelect = async (slot) => {
    try {
      // Check for duplicate booking using the API before allowing selection
      const dateStr = selectedDate.toISOString().split("T")[0];
      console.log("Checking booking for slot:", slot.id, "date:", dateStr);

      const response = await ApiService.checkUserBookingExists(
        slot.id,
        dateStr
      );
      console.log("API response:", response);

      if (response.data.hasBooking) {
        console.log("Duplicate booking found via API");
        setError(
          "You already have a booking for this time slot on this date. Please select a different slot or date."
        );
        return;
      }

      console.log("No duplicate booking found, selecting slot");
      setSelectedSlot(slot);
      setError(""); // Clear any previous errors
    } catch (error) {
      console.error("Error checking existing booking:", error);
      // Fall back to local check if API fails
      if (hasExistingBooking(slot.id, selectedDate)) {
        console.log("Duplicate booking found via local check");
        setError(
          "You already have a booking for this time slot on this date. Please select a different slot or date."
        );
        return;
      }
      console.log("No duplicate booking found via local check, selecting slot");
      setSelectedSlot(slot);
      setError(""); // Clear any previous errors
    }
  };
  const handlePurchaseAndBook = async () => {
    if (!selectedDate || !selectedSlot) {
      setError("Please select both a date and time slot");
      return;
    }

    try {
      // Double-check for duplicate booking before proceeding with payment
      const dateStr = selectedDate.toISOString().split("T")[0];
      const response = await ApiService.checkUserBookingExists(
        selectedSlot.id,
        dateStr
      );

      if (response.data.hasBooking) {
        setError(
          "You already have a booking for this time slot on this date. Please select a different slot or date."
        );
        setPurchasing(false);
        return;
      }

      setPurchasing(true);
      setError("");

      // Create purchase with optional coupon code
      const purchaseData = {
        pricingPlanId: selectedPlan.id,
        carTypeId: selectedCarType.id,
        paymentMethod: "razorpay",
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
      };

      const purchaseResponse = await ApiService.createPurchase(purchaseData);
      const { purchase, razorpayOrder, razorpayKeyId } = purchaseResponse.data;

      // Check if this is a zero-amount purchase (completed with coupon)
      if (purchase.paymentStatus === "completed") {
        // Purchase completed for free, now create booking
        await createBooking();
        return;
      }

      // Regular payment flow for non-zero amounts
      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () =>
          initializePayment(purchase, razorpayOrder, razorpayKeyId);
        document.body.appendChild(script);
      } else {
        initializePayment(purchase, razorpayOrder, razorpayKeyId);
      }
    } catch (error) {
      console.error("Purchase error:", error);
      setError(error.message || "Purchase failed. Please try again.");
      setPurchasing(false);
    }
  };

  const initializePayment = (purchase, razorpayOrder, razorpayKeyId) => {
    const options = {
      key: razorpayKeyId,
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency,
      name: "Steam & Drive",
      description: `${selectedPlan.name} for ${selectedCarType.name}`,
      order_id: razorpayOrder.id,
      handler: async (response) => {
        try {
          // Complete the purchase with payment details
          const completionData = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
          };
          await ApiService.completePurchase(purchase.id, completionData);

          // Clear any previous errors
          setError("");

          // After successful payment, create booking
          await createBooking();
        } catch (error) {
          console.error("Payment completion error:", error);
          setError("Payment verification failed. Please contact support.");
          setPurchasing(false);
        }
      },
      prefill: {
        name: currentUser?.displayName || "",
        email: currentUser?.email || "",
      },
      theme: {
        color: "#3B82F6",
      },
      modal: {
        ondismiss: () => {
          setPurchasing(false);
          setError("Payment was cancelled");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };
  const createBooking = async () => {
    try {
      const bookingData = {
        slotId: selectedSlot.id,
        bookingDate: selectedDate.toISOString().split("T")[0],
        serviceType: "pay_per_use",
      };

      const response = await ApiService.createBooking(bookingData);
      const bookingResult = response.data || response;

      setSuccess("Payment successful and booking confirmed!");

      // Redirect to booking confirmation page with booking details
      setTimeout(() => {
        navigate("/booking-confirmation", {
          state: {
            bookingDetails: {
              bookingId: bookingResult.id,
              bookingDate: selectedDate.toISOString().split("T")[0],
              slot: selectedSlot,
              serviceType: "pay_per_use",
            },
          },
        });
      }, 2000);
    } catch (error) {
      console.error("Booking creation error:", error);
      setError(
        "Payment successful, but booking failed. Please contact support."
      );
    } finally {
      setPurchasing(false);
    }
  };

  const generateCalendarDays = () => {
    const today = new Date();
    const days = [];

    // Generate next 10 days
    for (let i = 0; i < 10; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }

    return days;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    return timeStr;
  };

  const getPlanPrice = () => {
    if (!selectedCarType) return "N/A";

    const pricingInfo = selectedPlan.pricingPlanCarTypes?.find(
      (pct) => pct.carTypeId === selectedCarType.id && pct.enabled
    );

    if (!pricingInfo) return "N/A";

    const originalPrice = parseFloat(pricingInfo.price);

    if (appliedCoupon) {
      const discountAmount = parseFloat(appliedCoupon.discountAmount);
      const finalPrice = Math.max(0, originalPrice - discountAmount);
      return `₹${finalPrice.toFixed(2)}`;
    }

    return `₹${originalPrice.toFixed(2)}`;
  };

  if (!selectedPlan || !selectedCarType) {
    return <div>Loading...</div>;
  }
  return (
    <div className="pay-per-use-slot-booking">
      <header className="ppusb-header">
        <div className="ppusb-header-content">
          <img src={logo} alt="Steam & Drive" className="ppusb-logo" />
          <div className="ppusb-user-info">
            <span>
              Welcome, {currentUser?.displayName || currentUser?.email}
            </span>
            <button onClick={handleLogout} className="ppusb-logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="ppusb-container">
        <div className="ppusb-content">
          {" "}
          <div className="ppusb-booking-header">
            <button
              onClick={() => navigate("/integrated-purchase-flow")}
              className="ppusb-back-button"
            >
              ← Back
            </button>
            <h2>Book Your Pay-Per-Use Service</h2>
            <p>
              Select your preferred date and time slot, then complete payment
            </p>
          </div>
          {error && (
            <div className="ppusb-error-message">
              <p>{error}</p>
            </div>
          )}
          {success && (
            <div className="ppusb-success-message">
              <p>{success}</p>
            </div>
          )}
          {/* Plan Summary */}
          <div className="ppusb-plan-summary">
            <h3>Selected Plan</h3>
            <div className="ppusb-plan-details">
              <div className="ppusb-detail-row">
                <span>Plan:</span>
                <span>{selectedPlan.name}</span>
              </div>
              <div className="ppusb-detail-row">
                <span>Car Type:</span>
                <span>{selectedCarType.name}</span>
              </div>
              <div className="ppusb-detail-row">
                <span>Pay-Per-Use Credits:</span>
                <span>{selectedPlan.oneTimeCreditsGiven}</span>
              </div>
              <div className="ppusb-detail-row">
                <span>Price:</span>
                <span>{getPlanPrice()}</span>
              </div>
            </div>
          </div>
          {/* Date Selection */}
          <div className="ppusb-date-selection">
            <h3>Select Date</h3>
            <div className="ppusb-calendar-grid">
              {generateCalendarDays().map((date, index) => {
                const isDisabled = isDateDisabled(date);
                return (
                  <div
                    key={index}
                    className={`ppusb-calendar-day ${
                      selectedDate?.toDateString() === date.toDateString()
                        ? "selected"
                        : ""
                    } ${isDisabled ? "disabled" : ""}`}
                    onClick={() => handleDateSelect(date)}
                    title={isDisabled ? getDisabledDateReason(date) : ""}
                  >
                    <div className="ppusb-day-name">{formatDate(date)}</div>
                    <div className="ppusb-day-number">{date.getDate()}</div>
                    {isDisabled && (
                      <div className="ppusb-disabled-indicator">🚫</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>{" "}
          {/* Time Slot Selection */}
          {selectedDate && (
            <div className="ppusb-slot-selection">
              <h3>Select Time Slot for {formatDate(selectedDate)}</h3>

              {loading ? (
                <div className="ppusb-loading-slots">
                  <div className="ppusb-spinner"></div>
                  <p>Loading available slots...</p>
                </div>
              ) : (
                <div className="ppusb-slots-grid">
                  {availableSlots.map((slot) => {
                    const hasBooking = hasExistingBooking(
                      slot.id,
                      selectedDate
                    );
                    return (
                      <div
                        key={slot.id}
                        className={`ppusb-time-slot ${
                          !slot.isAvailable ? "unavailable" : ""
                        } ${hasBooking ? "already-booked" : ""} ${
                          selectedSlot?.id === slot.id ? "selected" : ""
                        }`}
                        onClick={() =>
                          slot.isAvailable &&
                          !hasBooking &&
                          handleSlotSelect(slot)
                        }
                        title={
                          hasBooking
                            ? "You already have a booking for this slot"
                            : ""
                        }
                      >
                        <div className="ppusb-slot-time">
                          {formatTime(slot.startTime)} -{" "}
                          {formatTime(slot.endTime)}
                        </div>
                        <div className="ppusb-slot-availability">
                          {hasBooking ? (
                            <span className="ppusb-already-booked-text">
                              Already booked by you
                            </span>
                          ) : slot.isAvailable ? (
                            `${slot.availableCapacity} slot${
                              slot.availableCapacity !== 1 ? "s" : ""
                            } available`
                          ) : (
                            "Fully booked"
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!loading && availableSlots.length === 0 && (
                <div className="ppusb-no-slots">
                  <p>No time slots available for the selected date.</p>
                  <p>Please choose a different date.</p>
                </div>
              )}
            </div>
          )}
          {/* Purchase and Book Button */}
          {selectedDate && selectedSlot && (
            <div className="ppusb-purchase-section">
              <div className="ppusb-booking-summary">
                <h3>Booking Summary</h3>
                <div className="ppusb-summary-details">
                  <div className="ppusb-summary-row">
                    <span>Date:</span>
                    <span>{formatDate(selectedDate)}</span>
                  </div>
                  <div className="ppusb-summary-row">
                    <span>Time:</span>
                    <span>
                      {formatTime(selectedSlot.startTime)} -{" "}
                      {formatTime(selectedSlot.endTime)}
                    </span>
                  </div>
                  <div className="ppusb-summary-row">
                    <span>Plan:</span>
                    <span>{selectedPlan.name}</span>
                  </div>
                  <div className="ppusb-summary-row total">
                    <span>Total Price:</span>
                    <span>{getPlanPrice()}</span>
                  </div>
                </div>
              </div>{" "}
              <button
                onClick={handlePurchaseAndBook}
                disabled={purchasing}
                className="ppusb-purchase-and-book-button"
              >
                {purchasing ? "Processing..." : "Purchase & Book Service"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayPerUseSlotBooking;
